// GitHub-style year grid coloured by daily P&L. Each cell is one trading
// day; rows are weekdays Mon-Fri (no weekends shown); columns are weeks.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Tooltip from '@radix-ui/react-tooltip';
import { api } from '@/lib/api';
import { fmtDate, fmtSignedMoney, fmtSignedPercent } from '@/lib/format';
import { cn } from '@/lib/cn';

type Day = { date: string; ret: number; pnl: number };

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function colorFor(ret: number, maxAbs: number): string {
  if (Number.isNaN(ret) || maxAbs <= 0) return 'rgba(63, 63, 70, 0.4)';
  const intensity = Math.min(1, Math.abs(ret) / maxAbs);
  const alpha = 0.18 + intensity * 0.72;
  if (ret >= 0) return `rgba(52, 211, 153, ${alpha.toFixed(3)})`;
  return `rgba(251, 113, 133, ${alpha.toFixed(3)})`;
}

export function PnlCalendarHeatmap() {
  const q = useQuery({ queryKey: ['performance'], queryFn: api.performance });
  const [showWeekends] = useState(false);

  const { weeks, maxAbs, total } = useMemo(() => {
    if (!q.data) return { weeks: [], maxAbs: 0, total: 0 };
    const map = new Map<string, Day>();
    let prevValue = 0;
    for (const d of q.data.risk.dailyReturns) {
      const pnl = prevValue > 0 ? d.value - prevValue : 0;
      map.set(d.date, { date: d.date, ret: d.ret, pnl });
      prevValue = d.value;
    }
    // Build the 12-month window ending today.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setMonth(start.getMonth() - 12);
    // Snap to Monday
    const dow = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - dow);

    const weeksArr: Array<Array<Day | null>> = [];
    let cursor = new Date(start);
    let maxAbsVal = 0;
    let totalP = 0;
    while (cursor <= today) {
      const week: Array<Day | null> = [];
      for (let d = 0; d < 7; d += 1) {
        if (!showWeekends && (d === 5 || d === 6)) {
          cursor.setDate(cursor.getDate() + 1);
          continue;
        }
        const key = cursor.toISOString().slice(0, 10);
        const day = map.get(key) ?? null;
        if (day) {
          maxAbsVal = Math.max(maxAbsVal, Math.abs(day.ret));
          totalP += day.pnl;
        }
        week.push(day);
        cursor.setDate(cursor.getDate() + 1);
      }
      weeksArr.push(week);
    }
    return { weeks: weeksArr, maxAbs: maxAbsVal, total: totalP };
  }, [q.data, showWeekends]);

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-medium text-zinc-200">P&L calendar</h3>
          <p className="text-[11px] text-zinc-400">
            Last 12 months · trading days · {fmtSignedMoney(total)} total
          </p>
        </div>
        <Legend />
      </div>

      <Tooltip.Provider delayDuration={50}>
        <div className="flex gap-1 overflow-x-auto pb-1">
          <div className="flex flex-col gap-1 mt-3 mr-1 text-[9px] text-zinc-400 shrink-0">
            {WEEKDAYS.map((d) => (
              <span key={d} className="h-3 flex items-center">
                {d}
              </span>
            ))}
          </div>
          <div className="flex gap-1">
            {weeks.map((week, wi) => (
              <div key={wi} className="flex flex-col gap-1">
                {week.map((day, di) => (
                  <Cell key={di} day={day} maxAbs={maxAbs} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </Tooltip.Provider>
    </div>
  );
}

function Cell({ day, maxAbs }: { day: Day | null; maxAbs: number }) {
  if (!day) {
    return (
      <span
        className="block w-3 h-3 rounded-sm"
        style={{ background: 'rgba(39, 39, 47, 0.5)' }}
      />
    );
  }
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <span
          className="block w-3 h-3 rounded-sm cursor-pointer hover:ring-2 hover:ring-zinc-700"
          style={{ background: colorFor(day.ret, maxAbs) }}
        />
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          sideOffset={6}
          className="rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-[11px] text-zinc-200 shadow-xl"
        >
          <div className="text-zinc-400">{fmtDate(day.date)}</div>
          <div className={cn(day.ret >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
            {fmtSignedPercent(day.ret * 100, 2)} ({fmtSignedMoney(day.pnl)})
          </div>
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-1 text-[10px] text-zinc-400">
      <span>Less</span>
      <span className="w-3 h-3 rounded-sm" style={{ background: 'rgba(251, 113, 133, 0.8)' }} />
      <span className="w-3 h-3 rounded-sm" style={{ background: 'rgba(251, 113, 133, 0.35)' }} />
      <span className="w-3 h-3 rounded-sm bg-zinc-800" />
      <span className="w-3 h-3 rounded-sm" style={{ background: 'rgba(52, 211, 153, 0.35)' }} />
      <span className="w-3 h-3 rounded-sm" style={{ background: 'rgba(52, 211, 153, 0.8)' }} />
      <span>More</span>
    </div>
  );
}
